import { mountRepAdmin } from "./my-ecofoot-admin.js?v=phase4-manage-ux-20261008";

const REMEMBERED_FOR_MS = 30 * 24 * 60 * 60 * 1000;
const APPROVED_LOGO_ROOT = "https://cdn.shopify.com/s/files/";

export function mountAiPointerHover(root) {
  const button = root.querySelector(".my-ecofoot__ai-action");
  if (!button || typeof window.matchMedia !== "function") return;
  const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const leave = () => button.removeAttribute("data-ai-hover-active");
  const track = (event) => {
    if (event.pointerType !== "mouse" || !finePointer.matches || reducedMotion.matches) {
      leave();
      return;
    }
    const bounds = button.getBoundingClientRect();
    const x = Math.max(0, Math.min(bounds.width, event.clientX - bounds.left));
    const y = Math.max(0, Math.min(bounds.height, event.clientY - bounds.top));
    button.style.setProperty("--hover-x", `${x}px`);
    button.style.setProperty("--hover-y", `${y}px`);
    button.setAttribute("data-ai-hover-active", "true");
  };
  button.addEventListener("pointerenter", track);
  button.addEventListener("pointermove", track);
  button.addEventListener("pointerleave", leave);
  button.addEventListener("pointercancel", leave);
  finePointer.addEventListener("change", leave);
  reducedMotion.addEventListener("change", leave);
}

export function normalizeRememberedEmail(value) {
  if (typeof value !== "string") return null;
  const email = value.trim().toLowerCase();
  if (
    !email ||
    email.length > 254 ||
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  ) {
    return null;
  }
  return email;
}

export function forgetRememberedEmail(storage, key) {
  try {
    storage.removeItem(key);
  } catch (_error) {
    // Storage can be unavailable in privacy-restricted browsers.
  }
}

export function readRememberedEmail(storage, key, now = Date.now()) {
  try {
    const value = JSON.parse(storage.getItem(key) || "null");
    const email = normalizeRememberedEmail(value?.email);
    const savedAt = Number(value?.savedAt);
    if (
      !email ||
      !Number.isFinite(savedAt) ||
      savedAt > now ||
      now - savedAt > REMEMBERED_FOR_MS
    ) {
      forgetRememberedEmail(storage, key);
      return null;
    }
    return email;
  } catch (_error) {
    forgetRememberedEmail(storage, key);
    return null;
  }
}

export function rememberEmail(storage, key, email, now = Date.now()) {
  const normalized = normalizeRememberedEmail(email);
  if (!normalized) return false;
  try {
    storage.setItem(key, JSON.stringify({ email: normalized, savedAt: now }));
    return true;
  } catch (_error) {
    return false;
  }
}

export function normalizeDisplayName(value) {
  if (typeof value !== "string") return "";
  return value
    .replace(/[\u0000-\u001f\u007f]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 200);
}

function validAuthorizedResponse(value) {
  return Boolean(
    value &&
    value.status === "authorized" &&
    value.account &&
    typeof value.account.name === "string" &&
    typeof value.account.email === "string" &&
    ["MEMBER", "ORG_ADMIN"].includes(value.account.role) &&
    value.organization &&
    typeof value.organization.name === "string" &&
    (value.organization.logoUrl === null ||
      typeof value.organization.logoUrl === "string") &&
    value.capabilities?.canViewPricing === true &&
    typeof value.capabilities?.canRequestTeamElevation === "boolean",
  );
}

async function requestAccess(endpoint, email) {
  const response = await fetch(endpoint, {
    method: "POST",
    credentials: "same-origin",
    cache: "no-store",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) throw new Error("Access check failed");
  return response.json();
}

export async function renewAdminTicket(endpoint, email, organizationName) {
  try {
    const result = await requestAccess(endpoint, email);
    if (!validAuthorizedResponse(result) || result.account.role !== "ORG_ADMIN" ||
        !result.capabilities.canRequestTeamElevation || result.account.email !== email ||
        result.organization.name !== organizationName ||
        !/^[A-Za-z0-9_-]{43}$/.test(result.management?.token || "")) throw new Error("Unavailable");
    return result.management.token;
  } catch {
    throw new Error("We couldn't start verification right now. Please try again.");
  }
}

function mount(root) {
  if (root.dataset.myEcofootMounted === "true") return;
  root.dataset.myEcofootMounted = "true";
  mountAiPointerHover(root);

  const endpoint = root.dataset.accessEndpoint;
  const storageKey = root.dataset.storageKey;
  const panels = Object.fromEntries(
    Array.from(root.querySelectorAll("[data-my-ecofoot-panel]")).map(
      (panel) => [panel.dataset.myEcofootPanel, panel],
    ),
  );
  const form = root.querySelector("[data-my-ecofoot-form]");
  const emailInput = form.querySelector('input[name="email"]');
  const notice = root.querySelector("[data-my-ecofoot-notice]");
  const noticeText = root.querySelector("[data-my-ecofoot-notice-text]");
  const signOut = root.querySelector("[data-my-ecofoot-sign-out]");
  const management = mountRepAdmin(root, {
    renewTicket: (email, organizationName) => renewAdminTicket(endpoint, email, organizationName),
  });

  function showPanel(name) {
    if (name !== "authorized") management?.reset();
    Object.entries(panels).forEach(([panelName, panel]) => {
      panel.hidden = panelName !== name;
    });
    root.dataset.state = name;
    root.setAttribute("aria-busy", name === "loading" ? "true" : "false");
  }

  function showSignedOut(message = "") {
    notice.hidden = !message;
    noticeText.textContent = message;
    showPanel("signed-out");
  }

  function renderAuthorized(result) {
    const accountName = normalizeDisplayName(result.account.name);
    const roleLabel =
      result.account.role === "ORG_ADMIN" ? "Organization Admin" : "Member";
    const firstName = accountName.split(" ")[0] || "";

    root.querySelector("[data-my-ecofoot-name]").textContent = accountName;
    root.querySelector("[data-my-ecofoot-email]").textContent =
      result.account.email;
    root.querySelector("[data-my-ecofoot-role]").textContent = roleLabel;
    root.querySelector("[data-my-ecofoot-card-name]").textContent = accountName;
    root.querySelector("[data-my-ecofoot-card-role]").textContent = roleLabel;
    root.querySelector("[data-my-ecofoot-first-name]").textContent = firstName
      ? `, ${firstName}`
      : "";

    const logo = root.querySelector("[data-my-ecofoot-logo]");
    const organizationFallback = root.querySelector(
      "[data-my-ecofoot-card-organization]",
    );
    organizationFallback.textContent = result.organization.name;
    if (result.organization.logoUrl?.startsWith(APPROVED_LOGO_ROOT)) {
      logo.src = result.organization.logoUrl;
      logo.alt = `${result.organization.name} logo`;
      logo.hidden = false;
      organizationFallback.hidden = true;
    } else {
      logo.removeAttribute("src");
      logo.alt = "";
      logo.hidden = true;
      organizationFallback.hidden = false;
    }

    const repCard = root.querySelector("[data-my-ecofoot-rep-card]");
    repCard.classList.remove("is-revealing");
    requestAnimationFrame(() => repCard.classList.add("is-revealing"));
    showPanel("authorized");
    management?.recognized(result);
  }

  async function recognize(email, remembered) {
    showPanel("loading");
    try {
      const result = await requestAccess(endpoint, email);
      if (!validAuthorizedResponse(result)) {
        forgetRememberedEmail(localStorage, storageKey);
        emailInput.value = remembered ? "" : email;
        showSignedOut("This email does not currently have Rep Portal access.");
        return;
      }
      rememberEmail(localStorage, storageKey, result.account.email);
      renderAuthorized(result);
    } catch (_error) {
      emailInput.value = email;
      showSignedOut("We couldn't check access right now. Please try again.");
    }
  }

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const email = normalizeRememberedEmail(emailInput.value);
    if (!email) {
      emailInput.setCustomValidity("Enter a valid email address.");
      emailInput.reportValidity();
      return;
    }
    emailInput.setCustomValidity("");
    recognize(email, false);
  });

  emailInput.addEventListener("input", () => emailInput.setCustomValidity(""));
  signOut.addEventListener("click", () => {
    forgetRememberedEmail(localStorage, storageKey);
    emailInput.value = "";
    showSignedOut();
    emailInput.focus();
  });

  const rememberedEmail = readRememberedEmail(localStorage, storageKey);
  if (rememberedEmail) {
    recognize(rememberedEmail, true);
  } else {
    showSignedOut();
  }
}

export function mountMyEcofoot(scope = document) {
  scope.querySelectorAll("[data-my-ecofoot]").forEach(mount);
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => mountMyEcofoot());
  } else {
    mountMyEcofoot();
  }
  document.addEventListener("shopify:section:load", (event) =>
    mountMyEcofoot(event.target),
  );
}
